import { GROUP_BADGES, GroupBadge } from '../../hooks/useGroupBadges';

interface GroupBadgeDisplayProps {
  badges: string[];
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  maxDisplay?: number;
}

export default function GroupBadgeDisplay({ 
  badges, 
  size = 'md',
  showTooltip = true,
  maxDisplay = 3
}: GroupBadgeDisplayProps) {
  if (!badges || badges.length === 0) return null;

  const sizeClasses = {
    sm: 'w-5 h-5 text-xs',
    md: 'w-6 h-6 text-sm',
    lg: 'w-8 h-8 text-base'
  };

  const displayBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  const getBadgeInfo = (badgeId: string): GroupBadge | undefined => {
    return GROUP_BADGES.find(b => b.id === badgeId);
  };

  return (
    <div className="flex items-center gap-1">
      {displayBadges.map((badgeId) => {
        const badge = getBadgeInfo(badgeId);
        if (!badge) return null;

        return (
          <div
            key={badgeId}
            className={`${sizeClasses[size]} ${badge.color} rounded-full flex items-center justify-center relative group cursor-pointer`}
            title={showTooltip ? `${badge.name}: ${badge.description}` : undefined}
          >
            <i className={badge.icon}></i>
            
            {showTooltip && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                <div className="font-semibold">{badge.name}</div>
                <div className="text-gray-300">{badge.description}</div>
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                  <div className="border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {remainingCount > 0 && (
        <div
          className={`${sizeClasses[size]} bg-gray-100 text-gray-600 rounded-full flex items-center justify-center font-medium`}
          title={`+${remainingCount} more badges`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
